import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, Phone, Mail } from "lucide-react";

const LINKS = [
  { to: "/", label: "Home", testid: "nav-home" },
  { to: "/services", label: "Services", testid: "nav-services" },
  { to: "/regions", label: "Regions", testid: "nav-regions" },
  { to: "/cities", label: "Cities", testid: "nav-cities" },
  { to: "/blog", label: "Journal", testid: "nav-blog" },
  { to: "/about", label: "About", testid: "nav-about" },
  { to: "/contact", label: "Contact", testid: "nav-contact" },
];

export const PHONE = "(807) 907-2874";
export const PHONE_HREF = "tel:+18079072874";
export const EMAIL = "info@mazesecurity.ca";
export const EMAIL_HREF = "mailto:info@mazesecurity.ca";

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-[#F4F4F0]/95 backdrop-blur-md border-b border-black/10" : "bg-[#F4F4F0]/70 backdrop-blur-sm"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-logo"
          className="group flex items-center gap-3 text-[#060913]"
        >
          <span className="font-display text-2xl md:text-3xl leading-none w-10 h-10 md:w-11 md:h-11 flex items-center justify-center bg-[#060913] text-[#F4F4F0] group-hover:bg-[#D92323] transition-colors">
            M
          </span>
          <span className="block leading-tight">
            <span className="block font-display text-[11px] sm:text-sm md:text-base tracking-tight text-[#060913]">
              MAZE SECURITY SOLUTIONS
            </span>
            <span className="block font-mono text-[7px] sm:text-[9px] tracking-[0.22em] sm:tracking-[0.24em] uppercase text-[#D92323] mt-0.5">
              British Columbia Security Services
            </span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-6 lg:gap-8">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              end={l.to === "/"}
              className={({ isActive }) =>
                `font-mono text-[10px] lg:text-[11px] tracking-[0.18em] lg:tracking-[0.22em] uppercase transition-colors ${
                  isActive ? "text-[#D92323]" : "text-[#060913] hover:text-[#D92323]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden inline-flex items-center justify-center w-10 h-10 border border-black/20"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {/* Contact info strip — visible on desktop + mobile */}
      <div className="border-t border-black/10 bg-[#060913] text-[#F4F4F0]" data-testid="nav-contact-strip">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-2.5 flex flex-wrap items-center justify-center md:justify-between gap-x-6 gap-y-1.5">
          <a
            href={PHONE_HREF}
            data-testid="nav-phone"
            className="inline-flex items-center gap-2 font-mono text-[11px] md:text-[12px] tracking-[0.18em] uppercase hover:text-[#D92323] transition-colors"
          >
            <Phone size={14} strokeWidth={1.5} className="text-[#D92323]" />
            {PHONE}
          </a>
          <span className="hidden md:inline font-mono text-[10px] tracking-[0.24em] uppercase text-[#F4F4F0]/60">
            24 / 7 Dispatch · Province-Wide
          </span>
          <a
            href={EMAIL_HREF}
            data-testid="nav-email"
            className="inline-flex items-center gap-2 font-mono text-[11px] md:text-[12px] tracking-[0.14em] lowercase hover:text-[#D92323] transition-colors"
          >
            <Mail size={14} strokeWidth={1.5} className="text-[#D92323]" />
            {EMAIL}
          </a>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-black/10 bg-[#F4F4F0]">
          <div className="px-6 py-6 flex flex-col gap-4">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`${l.testid}-mobile`}
                className="font-mono text-[12px] tracking-[0.22em] uppercase text-[#060913]"
              >
                {l.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
